import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Plus, Eye, EyeOff, Pencil, History, Package, Trash2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { loadUploadWidget } from "../../lib/cloudinary";
import { menuImageUrl } from "../../data/cateringMenu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const UNITS = ["TRAY", "GUEST", "PORT", "GLASS", "SLICE", "BOARD", "JUG", "BULK"];
const naira = (n) => "\u20a6" + Math.round(n || 0).toLocaleString("en-NG");

export default function CateringManager() {
  const [cats, setCats] = useState([]);
  const [packages, setPackages] = useState([]);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [itemModal, setItemModal] = useState(null);
  const [catModal, setCatModal] = useState(null);
  const [pkgModal, setPkgModal] = useState(null);
  const [config, setConfig] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/menu").then(({ data }) => setCats(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/admin/packages").then(({ data }) => setPackages(data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    api.get("/admin/cloudinary/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, [load]);

  const loadAudit = () => api.get("/admin/menu/audit").then(({ data }) => setAudit(data)).catch(() => {});

  const allItems = useMemo(() => cats.flatMap((c) => c.items.map((i) => ({ ...i, cat_name: c.name }))), [cats]);

  const patchItem = async (item, changes, confirmed = false) => {
    try {
      await api.patch(`/admin/menu/items/${item.id}`, confirmed ? { ...changes, confirm: true } : changes);
      load();
      return true;
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiError(e.response?.data?.detail);
      if (status === 409 && detail.startsWith("Large price change")) {
        if (window.confirm(detail.replace(" Resend with confirm=true.", " Continue?"))) return patchItem(item, changes, true);
        return false;
      }
      toast.error(detail);
      return false;
    }
  };

  const toggleItemVisible = (item) => patchItem(item, { visible: !item.visible }).then((ok) => ok && toast.success(item.visible ? "Hidden" : "Shown"));

  const saveItem = async (form) => {
    try {
      const allows = form.unit === "TRAY" ? (form.allows_extras !== false) : false;
      const extraPrice = form.extra_piece_price !== "" && form.extra_piece_price != null ? parseInt(form.extra_piece_price) : null;
      if (form.id) {
        const changes = { name: form.name, price: parseInt(form.price) || 0, unit: form.unit, note: form.note || null,
          extra_piece_price: extraPrice, allows_extras: allows };
        const ok = await patchItem({ id: form.id, price: form._origPrice }, changes);
        if (!ok) return;
      } else {
        await api.post("/admin/menu/items", { category_id: form.category_id, name: form.name, price: parseInt(form.price) || 0,
          unit: form.unit, note: form.note || null, extra_piece_price: extraPrice, allows_extras: allows });
        load();
      }
      setItemModal(null);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openImageWidget = async (item) => {
    if (!config?.configured) return toast.error("Cloudinary is not configured.");
    try {
      const cloudinary = await loadUploadWidget();
      const widget = cloudinary.createUploadWidget(
        {
          cloudName: config.cloud_name,
          apiKey: config.api_key,
          uploadSignature: async (cb, paramsToSign) => {
            try { const { data } = await api.post("/admin/cloudinary/signature", { params_to_sign: paramsToSign }); cb(data.signature); }
            catch { toast.error("Could not sign upload"); }
          },
          folder: "sapore/menu",
          sources: ["local", "camera", "url"],
          multiple: false,
          maxFiles: 1,
          cropping: true,
          croppingAspectRatio: 1,
          croppingShowDimensions: true,
          showSkipCropButton: false,
          maxImageWidth: 800,
          clientAllowedFormats: ["jpg", "jpeg", "png", "webp"],
          maxFileSize: 10000000,
          styles: { palette: { link: "#661361", action: "#661361", tabIcon: "#661361" } },
        },
        async (error, result) => {
          if (!error && result && result.event === "success") {
            try {
              await api.patch(`/admin/menu/items/${item.id}`, { image_public_id: result.info.public_id });
              load();
              toast.success("Image updated");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }
        }
      );
      widget.open();
    } catch { toast.error("Could not load the upload widget"); }
  };

  const saveCat = async (form) => {
    try {
      if (form.id) await api.patch(`/admin/menu/categories/${form.id}`, { name: form.name, unit_label: form.unit_label, note: form.note });
      else await api.post("/admin/menu/categories", { name: form.name, unit_label: form.unit_label, note: form.note });
      setCatModal(null);
      load();
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const savePkg = async (form) => {
    try {
      const body = { name: form.name, serves: parseInt(form.serves) || 1, description: form.description, lines: form.lines, visible: form.visible };
      if (form.id) await api.patch(`/admin/packages/${form.id}`, body);
      else await api.post("/admin/packages", body);
      setPkgModal(null);
      load();
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="catering-manager">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-serif text-3xl font-bold text-sapore-purple">Catering Menu</h1>
        <div className="flex gap-2">
          <button data-testid="catering-audit-toggle" onClick={() => { setShowAudit((s) => !s); if (!showAudit) loadAudit(); }} className="text-sm px-3 py-2 rounded-full border border-sapore-border text-sapore-inkSoft hover:text-sapore-purple flex items-center gap-1"><History size={15} /> Change log</button>
          <button data-testid="catering-add-category" onClick={() => setCatModal({ name: "", unit_label: "Each dish serves 12", note: "" })} className="text-sm px-4 py-2 rounded-full border border-sapore-purple text-sapore-purple font-medium hover:bg-sapore-goldSubtle">+ Category</button>
        </div>
      </div>
      <p className="text-sm text-sapore-inkSoft mb-6">Edit the Events &amp; Catering menu shown on the public /events page. Prices are whole Naira per unit. This is separate from the breakfast Menu Manager.</p>

      {showAudit && (
        <div data-testid="catering-audit-panel" className="mb-6 bg-white rounded-xl border border-sapore-border p-4 max-h-64 overflow-y-auto">
          <p className="font-medium text-sm text-sapore-purple mb-2">Recent price changes</p>
          {audit.length === 0 ? <p className="text-xs text-sapore-inkSoft">No changes recorded yet.</p> :
            audit.map((a) => (
              <div key={a.id} className="text-xs text-sapore-ink py-1 border-b border-sapore-border/50 last:border-0">
                <span className="font-medium">{a.field}</span> · {a.old_value == null ? "created" : `${a.old_value} → ${a.new_value}`} <span className="text-sapore-inkSoft">· {new Date(a.changed_at).toLocaleString()}</span>
              </div>
            ))}
        </div>
      )}

      <div className="space-y-8">
        {cats.map((c) => (
          <section key={c.id} data-testid={`catering-cat-${c.slug}`}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-serif text-xl text-sapore-purple flex items-center gap-2">
                {c.name} {!c.visible && <span className="text-xs bg-sapore-muted/20 text-sapore-muted px-2 py-0.5 rounded-full">Hidden</span>}
              </h2>
              <div className="flex gap-2">
                <button data-testid={`catering-edit-cat-${c.slug}`} onClick={() => setCatModal(c)} className="text-xs text-sapore-inkSoft hover:text-sapore-purple flex items-center gap-1"><Pencil size={13} /> Edit</button>
                <button data-testid={`catering-add-item-${c.slug}`} onClick={() => setItemModal({ category_id: c.id, name: "", price: "", unit: "TRAY", note: "" })} className="text-xs px-3 py-1 rounded-full bg-sapore-purple text-sapore-cream flex items-center gap-1"><Plus size={13} /> Item</button>
              </div>
            </div>
            <p className="text-xs text-sapore-inkSoft mb-3">{c.unit_label}{c.note ? ` — ${c.note}` : ""}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {c.items.map((item) => (
                <div key={item.id} data-testid={`catering-item-${item.id}`} className="bg-white rounded-xl border border-sapore-border p-3">
                  <div className="flex items-start gap-3">
                    {item.image_public_id && config?.cloud_name && (
                      <img src={menuImageUrl(item.image_public_id, config.cloud_name)} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm text-sapore-ink truncate">{item.name}</p>
                      <p className="text-xs text-sapore-gold font-semibold">{naira(item.price)} <span className="text-sapore-inkSoft font-normal">/ {item.unit}</span></p>
                      {item.unit === "TRAY" && item.allows_extras !== false && (
                        <p className="text-[11px] text-sapore-inkSoft">+ {naira(item.extra_piece_price || Math.round(item.price / 12))}/extra piece</p>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button data-testid={`catering-image-${item.id}`} onClick={() => openImageWidget(item)} title="Image" className="p-1 text-sapore-inkSoft hover:text-sapore-purple"><ImagePlus size={14} /></button>
                      <button data-testid={`catering-toggle-${item.id}`} onClick={() => toggleItemVisible(item)} title="Visible" className={`p-1 rounded ${item.visible ? "text-sapore-purple" : "text-sapore-muted"}`}>{item.visible ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                      <button data-testid={`catering-edit-item-${item.id}`} onClick={() => setItemModal({ ...item, _origPrice: item.price })} className="p-1 text-sapore-inkSoft hover:text-sapore-purple"><Pencil size={14} /></button>
                    </div>
                  </div>
                  {item.note && <p className="text-[11px] text-sapore-inkSoft mt-1">{item.note}</p>}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-12">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-2xl text-sapore-purple flex items-center gap-2"><Package size={20} /> Packages</h2>
          <button data-testid="catering-add-package" onClick={() => setPkgModal({ name: "", serves: 30, description: "", lines: [], visible: true })} className="text-sm px-4 py-2 rounded-full border border-sapore-purple text-sapore-purple font-medium hover:bg-sapore-goldSubtle">+ Package</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {packages.map((p) => (
            <div key={p.id} data-testid={`catering-package-${p.id}`} className="bg-white rounded-xl border border-sapore-border p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-sapore-ink">{p.name} {!p.visible && <span className="text-xs text-sapore-muted">(hidden)</span>}</p>
                  <p className="text-xs text-sapore-inkSoft">Serves ~{p.serves} · {p.lines?.length || 0} items</p>
                </div>
                <button data-testid={`catering-edit-package-${p.id}`} onClick={() => setPkgModal(p)} className="p-1 text-sapore-inkSoft hover:text-sapore-purple"><Pencil size={15} /></button>
              </div>
              <p className="text-xs text-sapore-inkSoft mt-2">{p.description}</p>
            </div>
          ))}
        </div>
      </div>

      {itemModal && <ItemEditor item={itemModal} onClose={() => setItemModal(null)} onSave={saveItem} />}
      {catModal && <CatEditor cat={catModal} onClose={() => setCatModal(null)} onSave={saveCat} />}
      {pkgModal && <PkgEditor pkg={pkgModal} allItems={allItems} onClose={() => setPkgModal(null)} onSave={savePkg} />}
    </div>
  );
}

function ItemEditor({ item, onClose, onSave }) {
  const [form, setForm] = useState({ ...item });
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="catering-item-editor">
        <DialogHeader><DialogTitle className="font-serif text-xl text-sapore-purple">{item.id ? "Edit item" : "New item"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="catering-item-name" placeholder="Name" value={form.name} onChange={(e) => up("name", e.target.value)} className="w-full rounded-xl border border-sapore-border p-3 text-sm" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-sapore-ink">Price (₦)</label>
              <input data-testid="catering-item-price" type="number" value={form.price} onChange={(e) => up("price", e.target.value)} className="w-full rounded-xl border border-sapore-border p-3 text-sm mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-sapore-ink">Unit</label>
              <select data-testid="catering-item-unit" value={form.unit} onChange={(e) => up("unit", e.target.value)} className="w-full rounded-xl border border-sapore-border p-3 text-sm mt-1 bg-white">
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <input data-testid="catering-item-note" placeholder="Note (optional)" value={form.note || ""} onChange={(e) => up("note", e.target.value)} className="w-full rounded-xl border border-sapore-border p-3 text-sm" />
          {form.unit === "TRAY" && (
            <div className="space-y-3 border-t border-sapore-border pt-3">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.allows_extras !== false} onChange={(e) => up("allows_extras", e.target.checked)} data-testid="catering-item-allows-extras" /> Allow extra pieces on top of a full tray</label>
              <div>
                <label className="text-xs font-medium text-sapore-ink">Extra piece price (₦)</label>
                <input data-testid="catering-item-extra-price" type="number" value={form.extra_piece_price ?? ""} onChange={(e) => up("extra_piece_price", e.target.value)} placeholder={`Default ${Math.round((parseInt(form.price) || 0) / 12)}`} className="w-full rounded-xl border border-sapore-border p-3 text-sm mt-1" />
              </div>
            </div>
          )}
          <button data-testid="catering-save-item" onClick={() => onSave(form)} className="w-full py-3 rounded-full bg-sapore-gold text-sapore-ink font-semibold hover:bg-sapore-goldHover">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CatEditor({ cat, onClose, onSave }) {
  const [form, setForm] = useState({ ...cat });
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="catering-cat-editor">
        <DialogHeader><DialogTitle className="font-serif text-xl text-sapore-purple">{cat.id ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="catering-cat-name" placeholder="Name" value={form.name} onChange={(e) => up("name", e.target.value)} className="w-full rounded-xl border border-sapore-border p-3 text-sm" />
          <input data-testid="catering-cat-unitlabel" placeholder="Unit label (e.g. Each dish serves 12)" value={form.unit_label || ""} onChange={(e) => up("unit_label", e.target.value)} className="w-full rounded-xl border border-sapore-border p-3 text-sm" />
          <textarea data-testid="catering-cat-note" placeholder="Note" value={form.note || ""} onChange={(e) => up("note", e.target.value)} rows={2} className="w-full rounded-xl border border-sapore-border p-3 text-sm" />
          <button data-testid="catering-save-cat" onClick={() => onSave(form)} className="w-full py-3 rounded-full bg-sapore-gold text-sapore-ink font-semibold hover:bg-sapore-goldHover">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PkgEditor({ pkg, allItems, onClose, onSave }) {
  const [form, setForm] = useState({ ...pkg, lines: pkg.lines || [] });
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const nameFor = (id) => allItems.find((i) => i.id === id)?.name || "(missing item)";
  const setLineQty = (idx, qty) => { const l = [...form.lines]; l[idx] = { ...l[idx], qty: Math.max(1, parseInt(qty) || 1) }; up("lines", l); };
  const removeLine = (idx) => up("lines", form.lines.filter((_, i) => i !== idx));
  const addLine = (item_id) => { if (item_id && !form.lines.some((l) => l.item_id === item_id)) up("lines", [...form.lines, { item_id, qty: 1 }]); };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto no-scrollbar" data-testid="catering-pkg-editor">
        <DialogHeader><DialogTitle className="font-serif text-xl text-sapore-purple">{pkg.id ? "Edit package" : "New package"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="catering-pkg-name" placeholder="Package name" value={form.name} onChange={(e) => up("name", e.target.value)} className="w-full rounded-xl border border-sapore-border p-3 text-sm" />
          <div className="grid grid-cols-2 gap-3 items-center">
            <div>
              <label className="text-xs font-medium text-sapore-ink">Serves ~</label>
              <input data-testid="catering-pkg-serves" type="number" value={form.serves} onChange={(e) => up("serves", e.target.value)} className="w-full rounded-xl border border-sapore-border p-3 text-sm mt-1" />
            </div>
            <label className="flex items-center gap-2 text-sm mt-5"><input type="checkbox" checked={form.visible} onChange={(e) => up("visible", e.target.checked)} data-testid="catering-pkg-visible" /> Visible</label>
          </div>
          <textarea data-testid="catering-pkg-desc" placeholder="Description" value={form.description} onChange={(e) => up("description", e.target.value)} rows={2} className="w-full rounded-xl border border-sapore-border p-3 text-sm" />
          <div>
            <p className="text-sm font-medium text-sapore-ink mb-2">Items in this package</p>
            {form.lines.map((l, i) => (
              <div key={l.item_id} className="flex gap-2 items-center mb-2" data-testid={`catering-pkg-line-${i}`}>
                <span className="flex-1 text-sm text-sapore-ink truncate">{nameFor(l.item_id)}</span>
                <input type="number" value={l.qty} onChange={(e) => setLineQty(i, e.target.value)} className="w-16 rounded-lg border border-sapore-border p-2 text-sm" />
                <button onClick={() => removeLine(i)} className="text-red-500"><Trash2 size={16} /></button>
              </div>
            ))}
            <select data-testid="catering-pkg-add-line" value="" onChange={(e) => addLine(e.target.value)} className="w-full rounded-xl border border-sapore-border p-3 text-sm mt-1 bg-white">
              <option value="">+ Add an item…</option>
              {allItems.map((i) => <option key={i.id} value={i.id}>{i.cat_name} — {i.name}</option>)}
            </select>
          </div>
          <button data-testid="catering-save-pkg" onClick={() => onSave(form)} className="w-full py-3 rounded-full bg-sapore-gold text-sapore-ink font-semibold hover:bg-sapore-goldHover">Save package</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
